/* Motion layer. Runs only when GSAP loaded and the visitor hasn't asked for reduced motion;
   otherwise theme.js's simple fade-ins handle everything. */
(() => {
  const { gsap, ScrollTrigger } = window;
  if (!gsap || !ScrollTrigger || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  gsap.registerPlugin(ScrollTrigger);
  document.documentElement.classList.add('has-gsap');

  const $ = (s, root = document) => root.querySelector(s);
  const $$ = (s, root = document) => [...root.querySelectorAll(s)];
  const finePointer = matchMedia('(pointer: fine)').matches;
  const rand = gsap.utils.random;

  /* ---------- split 3D headings into letters ---------- */
  function splitChars(el) {
    if (!el || el.dataset.split) return [];
    const text = el.textContent.trim().replace(/\s+/g, ' ');
    el.dataset.split = '1';
    el.setAttribute('aria-label', text);
    el.textContent = '';
    const chars = [];
    text.split(' ').forEach((word, i) => {
      if (i) el.append(' ');
      const w = document.createElement('span');
      w.className = 'split-word';
      w.setAttribute('aria-hidden', 'true');
      [...word].forEach((ch) => {
        const c = document.createElement('span');
        c.className = 'split-char';
        c.textContent = ch;
        w.append(c);
        chars.push(c);
      });
      el.append(w);
    });
    return chars;
  }

  /* ---------- hero entrance ---------- */
  const hero = $('.hero-product');
  if (hero) {
    const chars = splitChars($('.showcase__title', hero));
    const tl = gsap.timeline({ defaults: { ease: 'back.out(1.7)' } });
    const frame = $('.gallery__frame', hero);
    if (frame) tl.from(frame, { y: 70, rotation: -10, scale: 0.88, autoAlpha: 0, duration: 0.9 });
    const sticker = $('.sticker', hero);
    if (sticker) tl.from(sticker, { scale: 0, rotation: -45, duration: 0.5 }, '-=0.55');
    if (chars.length) tl.from(chars, { yPercent: 120, rotation: () => rand(-30, 30), autoAlpha: 0, stagger: 0.035, duration: 0.65 }, '-=0.35');
    const rest = $$('.showcase__sub, .price-row, .buy-form, .trust, .gallery__thumbs', hero);
    if (rest.length) tl.from(rest, { y: 26, autoAlpha: 0, stagger: 0.08, duration: 0.5, ease: 'power3.out' }, '-=0.35');

    // floating blobs: idle bob + parallax on scroll
    $$('.hero-product__blobs i', hero).forEach((b, i) => {
      gsap.to(b, { y: -18, rotation: i % 2 ? -10 : 10, duration: 2.4 + i, ease: 'sine.inOut', yoyo: true, repeat: -1 });
      gsap.to(b, {
        yPercent: -70 * (i + 1),
        ease: 'none',
        scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom top', scrub: true },
      });
    });

    // gallery frame tilts as you scroll past it
    if (frame) {
      gsap.to(frame, {
        rotation: 2,
        ease: 'none',
        scrollTrigger: { trigger: frame, start: 'top 60%', end: 'bottom top', scrub: true },
      });
    }
  }

  /* ---------- section headings: letters bounce in ---------- */
  $$('.section__head .text-3d, .statement__title, .empty-state .text-3d').forEach((h) => {
    if (hero && hero.contains(h)) return;
    const chars = splitChars(h);
    if (!chars.length) return;
    gsap.from(chars, {
      yPercent: 110,
      rotation: () => rand(-25, 25),
      autoAlpha: 0,
      stagger: 0.03,
      duration: 0.6,
      ease: 'back.out(2)',
      scrollTrigger: { trigger: h, start: 'top 88%', once: true },
    });
  });

  /* ---------- big statement: slow wobble while it's on screen ---------- */
  const statement = $('.statement__title');
  if (statement) {
    gsap.fromTo(statement, { rotation: -3, scale: 0.92 }, {
      rotation: 2,
      scale: 1.04,
      ease: 'none',
      scrollTrigger: { trigger: statement, start: 'top bottom', end: 'bottom top', scrub: true },
    });
  }

  /* ---------- everything else marked data-reveal ---------- */
  const reveals = $$('[data-reveal]').filter((el) => !(hero && hero.contains(el)));
  reveals.forEach((el) => {
    const i = $$('.feature', el.parentElement).indexOf(el);
    gsap.set(el, { autoAlpha: 0, y: 50, x: i < 0 ? 0 : (i % 2 ? 60 : -60), rotation: i < 0 ? 0 : (i % 2 ? 6 : -6) });
  });
  ScrollTrigger.batch(reveals, {
    start: 'top 90%',
    once: true,
    onEnter: (els) => gsap.to(els, { autoAlpha: 1, x: 0, y: 0, rotation: 0, stagger: 0.1, duration: 0.8, ease: 'back.out(1.5)' }),
  });

  /* ---------- marquees: infinite loop that speeds up while you scroll ---------- */
  const loops = $$('.marquee__track, .announce__track').map((track) => {
    const seconds = parseFloat(getComputedStyle(track).animationDuration) || 40;
    track.style.animation = 'none';
    const tween = gsap.to(track, { xPercent: -50, ease: 'none', duration: seconds, repeat: -1 });
    if (finePointer) {
      track.addEventListener('mouseenter', () => gsap.to(tween, { timeScale: 0.15, duration: 0.4 }));
      track.addEventListener('mouseleave', () => gsap.to(tween, { timeScale: 1, duration: 0.4 }));
    }
    return tween;
  });
  let boost = 0;
  ScrollTrigger.create({ onUpdate: (self) => { boost = Math.min(Math.abs(self.getVelocity()) / 350, 6); } });
  gsap.ticker.add(() => {
    if (boost < 0.01) return;
    boost *= 0.92;
    loops.forEach((t) => t.timeScale(1 + boost));
  });

  /* ---------- desktop: 3D tilt on the product photo ---------- */
  if (finePointer) {
    $$('.gallery__frame').forEach((el) => {
      gsap.set(el, { transformPerspective: 900 });
      const rx = gsap.quickTo(el, 'rotationX', { duration: 0.5, ease: 'power3' });
      const ry = gsap.quickTo(el, 'rotationY', { duration: 0.5, ease: 'power3' });
      el.addEventListener('pointermove', (e) => {
        const r = el.getBoundingClientRect();
        ry(((e.clientX - r.left) / r.width - 0.5) * 16);
        rx(-((e.clientY - r.top) / r.height - 0.5) * 16);
      });
      el.addEventListener('pointerleave', () => { rx(0); ry(0); });
    });

    // cards lift and straighten on hover, then settle back to their resting tilt
    $$('.polaroid, .feature, .product-card').forEach((el) => {
      const base = el.classList.contains('polaroid') ? gsap.getProperty(el, 'rotation') : 0;
      el.addEventListener('mouseenter', () => gsap.to(el, { scale: 1.06, rotation: 0, duration: 0.35, ease: 'back.out(2)', overwrite: 'auto' }));
      el.addEventListener('mouseleave', () => gsap.to(el, { scale: 1, rotation: base, duration: 0.5, ease: 'back.out(2)', overwrite: 'auto' }));
    });

    // magnetic buttons
    $$('.btn, .header__cart').forEach((b) => {
      const mx = gsap.quickTo(b, 'x', { duration: 0.4, ease: 'power3' });
      const my = gsap.quickTo(b, 'y', { duration: 0.4, ease: 'power3' });
      b.addEventListener('pointermove', (e) => {
        const r = b.getBoundingClientRect();
        mx((e.clientX - r.left - r.width / 2) * 0.25);
        my((e.clientY - r.top - r.height / 2) * 0.35);
      });
      b.addEventListener('pointerleave', () => { mx(0); my(0); });
    });
  }

  /* ---------- every tap squishes (phone and desktop) ---------- */
  document.addEventListener('pointerdown', (e) => {
    const t = e.target.closest('.btn, .chip, .gallery__thumb, .polaroid, .feature, .header__cart, .qty button');
    if (!t) return;
    gsap.fromTo(t, { scale: 0.9 }, { scale: 1, duration: 0.6, ease: 'elastic.out(1.2, 0.4)', overwrite: 'auto' });
  });

  /* ---------- add to cart: wig confetti + cart icon spin ---------- */
  const colours = ['var(--accent)', 'var(--accent-2)', 'var(--surface)'];
  document.addEventListener('cart:added', (e) => {
    const src = e.detail?.button || $('.header__cart');
    const r = src.getBoundingClientRect();
    const cx = r.left + r.width / 2;
    const cy = r.top + r.height / 2;
    for (let i = 0; i < 28; i++) {
      const p = document.createElement('span');
      p.className = i % 3 === 0 ? 'burst burst--wig' : 'burst burst--dot';
      if (i % 3) p.style.background = colours[i % colours.length];
      p.style.left = `${cx}px`;
      p.style.top = `${cy}px`;
      document.body.append(p);
      const angle = rand(0, Math.PI * 2);
      const dist = rand(70, 230);
      gsap.timeline({ onComplete: () => p.remove() })
        .to(p, { x: Math.cos(angle) * dist, y: Math.sin(angle) * dist - 70, rotation: rand(-360, 360), scale: rand(0.7, 1.4), duration: 0.6, ease: 'power3.out' })
        .to(p, { y: '+=200', autoAlpha: 0, duration: 0.8, ease: 'power2.in' }, '-=0.1');
    }
    gsap.fromTo('.header__cart', { rotation: 0 }, { rotation: 360, duration: 0.7, ease: 'back.out(1.8)' });
    gsap.fromTo('.header__count', { scale: 1.8 }, { scale: 1, duration: 0.6, ease: 'elastic.out(1.2, 0.4)' });
  });

  /* ---------- sold badge: little shake when it comes into view ---------- */
  $$('.sold-badge').forEach((b) => {
    gsap.fromTo(b, { rotation: -6 }, { rotation: 0, duration: 0.9, ease: 'elastic.out(1.4, 0.3)', scrollTrigger: { trigger: b, start: 'top 95%', once: true } });
  });

  // fonts change heading widths after load; re-measure trigger positions
  document.fonts?.ready.then(() => ScrollTrigger.refresh());
})();
